import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { PageHeader } from '../components/ui/PageHeader';
import { actionsApi, ActionItem } from '../services/apiService';

const ActionsCenter: React.FC = () => {
  const navigate = useNavigate();
  const [actions, setActions] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  useEffect(() => {
    fetchActions();
  }, []);

  const fetchActions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await actionsApi.getActions() as any;
      if (response?.success) {
        setActions(response.actions || []);
      }
    } catch (err: any) {
      console.error('Failed to fetch actions:', err);
      setError(err.message || 'Failed to load actions');
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (action: ActionItem, status: 'dismissed' | 'completed') => {
    try {
      setResolvingId(action._id);
      const response = await actionsApi.updateActionStatus(action._id, status) as any;
      if (response?.success) {
        setActions((prev) => prev.filter((a) => a._id !== action._id));
      }
    } catch (err: any) {
      console.error('Failed to update action:', err);
      setError(err.message || 'Failed to update action');
    } finally {
      setResolvingId(null);
    }
  };

  const handleAction = async (action: ActionItem) => {
    if (!action.ctaLink) return;
    // KB-health findings remain permanently discoverable on KB Overview, so
    // this card is an unread notification rather than the only route back to
    // unfinished work. Acknowledge it on entry; a later audit run will create
    // a fresh notification if live issues still need attention.
    if (action.type === 'kb_health') {
      try {
        setResolvingId(action._id);
        await actionsApi.completeActionsByType('kb_health');
        setActions((current) => current.filter((item) => item._id !== action._id));
      } catch (err: any) {
        console.error('Failed to acknowledge KB health action:', err);
        setError(err.message || 'Failed to update action');
        return;
      } finally {
        setResolvingId(null);
      }
    }
    navigate(action.ctaLink);
  };

  return (
    <MainLayout title="Actions Center">
      <div className="apexia-kb apexia-kb-page">
        <PageHeader title="Actions Center" description="Things worth your attention, in one place." />

        {error && (
          <div role="alert" className="mb-6 bg-error-tint border border-error text-error px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {loading ? (
          <div role="status" aria-label="Loading actions" className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-signature" />
          </div>
        ) : actions.length === 0 ? (
          <div className="text-center py-16 text-ink-muted">
            Nothing needs your attention right now.
          </div>
        ) : (
          <div className="space-y-3">
            {actions.map((action) => (
              <div
                key={action._id}
                className="bg-surface border border-hairline rounded-lg p-4 flex items-start justify-between gap-4"
              >
                <div>
                  <h3 className="font-semibold text-ink">{action.title}</h3>
                  {action.body && <p className="text-sm text-ink-secondary mt-1">{action.body}</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Onboarding isn't optional — dismissing it would leave the
                      account permanently without an entry point back, since
                      only genuine completion (not a click) clears it. Every
                      other action type stays dismissible. */}
                  {action.type !== 'kb_setup' && (
                    <button
                      onClick={() => handleResolve(action, 'dismissed')}
                      disabled={resolvingId === action._id}
                      className="px-3 py-1.5 text-sm text-ink-muted hover:text-ink-secondary disabled:opacity-50"
                    >
                      Dismiss
                    </button>
                  )}
                  {action.ctaLink && (
                    <button
                      onClick={() => handleAction(action)}
                      disabled={resolvingId === action._id}
                      className="px-4 py-1.5 bg-signature text-on-signature hover:bg-signature-strong text-sm rounded-lg disabled:opacity-50"
                    >
                      {action.ctaLabel || 'Go'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ActionsCenter;
